/**
 * Deteccao de dispositivo e de capacidades de entrada. Modulo PURO de leitura:
 * nao cria DOM, nao muda estilo, nao trava orientacao - so responde perguntas.
 *
 * CONTRATO (lote E e dono; Input, HUD, Menu e main consultam):
 *   isTouchDevice()      -> boolean   tem tela de toque de verdade
 *   isMobile()           -> boolean   celular/tablet (toque + viewport pequeno)
 *   isCoarsePointer()    -> boolean   media query pointer:coarse
 *   supportsPointerLock()-> boolean
 *   prefersTouchControls()-> boolean  decide se os controles na tela entram
 *   isLandscape()        -> boolean
 *   onOrientationChange(fn) -> funcao que remove o ouvinte
 *   requestFullscreen(el)-> Promise<boolean>  nunca lanca
 *   lockLandscape()      -> Promise<boolean>  nunca lanca (nao ha suporte no iOS)
 *   getDeviceInfo()      -> { touch, mobile, coarse, landscape, dpr, w, h }
 *
 * IMPORTANTE: nao decidir por user agent quando existir teste de capacidade.
 * UA mente (iPad se anuncia como Mac ha anos). `navigator.maxTouchPoints` e a
 * media query `pointer:coarse` nao mentem.
 */
export function isTouchDevice() {
  // Capacidade e a fonte da verdade; UA mente, inclusive no iPad que se anuncia como Mac.
  return typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
}

export function isMobile() {
  if (!isTouchDevice() || typeof window === 'undefined') return false;
  const w = Math.max(0, window.innerWidth || 0);
  const h = Math.max(0, window.innerHeight || 0);
  return Math.max(w, h) <= 1024;
}

export function isCoarsePointer() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
}

export function supportsPointerLock() {
  if (typeof document === 'undefined' || typeof Element === 'undefined') return false;
  return 'pointerLockElement' in document
    && typeof Element.prototype.requestPointerLock === 'function';
}

export function prefersTouchControls() {
  return isTouchDevice() && (isMobile() || isCoarsePointer());
}

export function isLandscape() {
  if (typeof window === 'undefined') return true;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(orientation: landscape)').matches;
  }
  return (window.innerWidth || 0) >= (window.innerHeight || 0);
}

export function onOrientationChange(fn) {
  if (typeof fn !== 'function' || typeof window === 'undefined') return () => false;

  const handler = () => fn();
  if (typeof window.matchMedia === 'function') {
    const query = window.matchMedia('(orientation: landscape)');
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', handler);
      return () => query.removeEventListener('change', handler);
    }
    if (typeof query.addListener === 'function') {
      query.addListener(handler);
      return () => query.removeListener(handler);
    }
  }

  window.addEventListener('orientationchange', handler);
  return () => window.removeEventListener('orientationchange', handler);
}

export async function requestFullscreen(el) {
  try {
    if (typeof document === 'undefined') return false;
    if (document.fullscreenElement || document.webkitFullscreenElement) return true;

    const target = el || document.documentElement;
    const request = target?.requestFullscreen || target?.webkitRequestFullscreen;
    if (typeof request !== 'function') return false;

    await Promise.resolve(request.call(target));
    return true;
  } catch (err) {
    // Recusa de gesto/permissao e comportamento normal, especialmente no iOS.
    return false;
  }
}

export async function lockLandscape() {
  try {
    const orientation = typeof screen !== 'undefined' ? screen.orientation : null;
    if (!orientation || typeof orientation.lock !== 'function') return false;
    await orientation.lock('landscape');
    return true;
  } catch (err) {
    // O iOS nao implementa lock e outros navegadores podem rejeitar fora de fullscreen.
    return false;
  }
}

export function getDeviceInfo() {
  const w = typeof window !== 'undefined' ? window.innerWidth || 0 : 0;
  const h = typeof window !== 'undefined' ? window.innerHeight || 0 : 0;
  return {
    touch: isTouchDevice(),
    mobile: isMobile(),
    coarse: isCoarsePointer(),
    landscape: isLandscape(),
    dpr: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
    w,
    h
  };
}
